'use client';

import useWindowSize from '@/hooks/use-window';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { memo, useCallback, useMemo, useState } from 'react';

import { useRouter } from '@shared/hooks/use-router';
import { ExternalLinkIcon, LinkIcon } from '@/components/icons/svgs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';

import { DEFAULT_FORM_TITLE } from '@/features/form-editing/components/constants/form-edit-constants';

import { IFormCardProps } from '@features/home-page/types/index';

import { CopyText, Tooltip } from '@/shared/components';
import { COPY } from '@/shared/constants/copies';
import { toast } from '@/shared/components/toast';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { CUSTOM_STAGES } from '@/shared/constants/stage';
import useConfirmDialog from '@/shared/hooks/useConfirmDialog';
import { createDuplicateForm, deleteForm, updateFormStatus } from '@/shared/services/forms/forms';
import { IFormUpdateStatusPayload } from '@/shared/services/forms/forms.types';
import { cancelAnchorNavigation } from '@/shared/utils/events';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';
import { openUrlInNewTab } from '@/shared/utils/window';

import { getFormEditPath, getFormFillPath } from '@/utils/common';
import { cn } from '@/utils/styles';

import { useFormStatusCache } from '../../hooks/use-dashboard-filters';
import { FormDropdownMenu } from './form-dropdown-menu';

// Form Status Component
const FormStatus = ({
	isDraft,
	applicationsCount,
	newCount,
	lastActivity,
}: {
	isDraft: boolean;
	applicationsCount: number;
	newCount: number;
	lastActivity?: string;
}) => {
	const displayNewCount = !!newCount && newCount < applicationsCount;

	return (
		<div className="mt-0">
			{!isDraft && (
				<div className="flex flex-wrap space-x-2 text-sm text-secondary md:space-x-4 md:text-sm">
					<span>
						{applicationsCount === 0
							? 'No candidates yet'
							: applicationsCount === 1
								? '1 Candidate'
								: `${applicationsCount} Candidates`}
					</span>
					{displayNewCount && (
						<span className="flex items-center gap-1 text-green-700">
							<svg
								width="16px"
								height="16px"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M22 7L14.1314 14.8686C13.7354 15.2646 13.5373 15.4627 13.309 15.5368C13.1082 15.6021 12.8918 15.6021 12.691 15.5368C12.4627 15.4627 12.2646 15.2646 11.8686 14.8686L9.13137 12.1314C8.73535 11.7354 8.53735 11.5373 8.30902 11.4632C8.10817 11.3979 7.89183 11.3979 7.69098 11.4632C7.46265 11.5373 7.26465 11.7354 6.86863 12.1314L2 17M22 7H15M22 7V14"
									stroke="currentColor"
									strokeWidth="2.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
							{newCount} New
						</span>
					)}
				</div>
			)}
			{!!isDraft && !!lastActivity && (
				<div className="flex space-x-4 text-sm text-secondary">
					<span className="flex items-center gap-1">
						<svg
							width="14px"
							height="14px"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M12 20H21M3.00003 20H4.67457C5.16376 20 5.40835 20 5.63852 19.9447C5.84259 19.8957 6.03768 19.8149 6.21663 19.7053C6.41846 19.5816 6.59141 19.4086 6.93732 19.0627L19.5001 6.49998C20.3285 5.67156 20.3285 4.32841 19.5001 3.49998C18.6716 2.67156 17.3285 2.67156 16.5001 3.49998L3.93729 16.0627C3.59139 16.4086 3.41843 16.5816 3.29475 16.7834C3.18509 16.9624 3.10428 17.1574 3.05529 17.3615C3.00003 17.5917 3.00003 17.8363 3.00003 18.3255V20Z"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						<span>Draft</span>
					</span>
				</div>
			)}
		</div>
	);
};

function FormCard({
	title,
	lastActivity,
	formStatus,
	formCounts,
	isLastCard = false,
	formId,
	isFocused = false,
}: IFormCardProps) {
	const router = useRouter();
	const queryClient = useQueryClient();
	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
	const { isMobile } = useWindowSize();
	const { getPreviousData, rollbackToPreviousData, updateFormStatusInCache } = useFormStatusCache();
	const { askConfirmation } = useConfirmDialog();

	const { mutate: createDuplicateFormMutation } = useMutation({
		mutationFn: createDuplicateForm,
		onSuccess: (data) => {
			router.push(getFormEditPath(data.formId));
		},
		retry: undefined,
	});

	const { mutateAsync: deleteFormMutationAsync } = useMutation({
		mutationFn: deleteForm,
		onSuccess: () => {
			toast({
				title: 'Form deleted successfully',
				variant: 'success',
			});
			queryClient.invalidateQueries();
			queryClient.refetchQueries({
				queryKey: ['forms'],
				type: 'active',
				exact: false,
			});
			router.refresh();
		},
	});

	const { mutate: updateFormStatusMutation, isPending: isUpdatingStatus } = useMutation<
		string,
		Error,
		IFormUpdateStatusPayload,
		{ previousData: ReturnType<typeof getPreviousData> }
	>({
		mutationFn: updateFormStatus,
		onMutate: async (variables) => {
			// Snapshot previous data for rollback
			const previousData = getPreviousData();

			// Optimistically update cache
			await updateFormStatusInCache(formId, variables.status);

			return { previousData };
		},
		onSuccess: (_data, variables) => {
			toast({
				title:
					variables.status === 'PUBLISHED' ? 'Accepting submissions' : 'Not accepting submissions',
				variant: 'success',
			});
		},
		onError: (_error, _variables, context) => {
			// Rollback to previous data
			if (context?.previousData) {
				rollbackToPreviousData(context.previousData);
			}
		},
		retry: undefined,
	});

	// handling status
	const isDraft = formStatus === 'DRAFT';

	const applicationsCount = formCounts.totalCount;

	const newCount = useMemo(() => {
		return (
			formCounts?.statusCounts?.find(
				(submissionStageCount) => submissionStageCount.stage === CUSTOM_STAGES.NEW
			)?.count ?? 0
		);
	}, [formCounts]);

	const canDeleteForm = true;

	const handleCardClick = useCallback(() => {
		router.push(getFormEditPath(formId));
	}, [router, formId]);

	const handleViewForm = (e: React.MouseEvent | React.KeyboardEvent) => {
		emitMixpanelEvent(mixpanelEvents.CLICKED_VIEW_FORM, {
			feature: MIXPANEL_APP_FEATURES.DASHBOARD,
			page: 'dashboard_page',
		});
		cancelAnchorNavigation(e);

		if (typeof window !== 'undefined') {
			openUrlInNewTab(getFormFillPath(formId));
		}
	};

	const handleCopyLink = async (e: React.MouseEvent | React.KeyboardEvent) => {
		emitMixpanelEvent(mixpanelEvents.CLICKED_COPY_FORM_LINK, {
			feature: MIXPANEL_APP_FEATURES.DASHBOARD,
			page: 'dashboard_page',
		});
		cancelAnchorNavigation(e);
		const formLink = getFormFillPath(formId);
		try {
			if (typeof navigator !== 'undefined')
				await navigator.clipboard.writeText(window.location.origin + formLink);

			toast({
				title: 'Link copied',
				variant: 'success',
			});
			setIsDropdownOpen(false);
		} catch (e) {
			toast({
				title: 'Failed to copy link',
				variant: 'destructive',
			});
		}
	};

	const handleDuplicate = () => {
		emitMixpanelEvent(mixpanelEvents.CLICKED_DUPLICATE_FORM, {
			feature: MIXPANEL_APP_FEATURES.DASHBOARD,
			page: 'dashboard',
		});

		if (isMobile) {
			toast({
				variant: 'dark',
				title: COPY.access.mobileUnavailable,
			});
			return;
		}

		createDuplicateFormMutation({
			form_id: formId,
		});
	};

	const handleEdit = () => {
		router.push(getFormEditPath(formId));
	};

	const handleDelete = useCallback(
		(e: React.MouseEvent | React.KeyboardEvent) => {
			cancelAnchorNavigation(e);
			if (!canDeleteForm) return;
			askConfirmation({
				title: COPY.confirms.areYouSure,
				description: (
					<CopyText value={COPY.confirms.deleteForm(title || DEFAULT_FORM_TITLE)} />
				),
				confirmButtonText: 'Yes, permanently delete',
				variant: 'delete',
				showProcessingWhileConfirming: true,
				disableKeyboardShortcut: true,
				onConfirm: () => deleteFormMutationAsync({ form_id: formId }),
			});
		},
		[canDeleteForm, askConfirmation, deleteFormMutationAsync, formId, title]
	);

	const handleToggleAcceptingApplications = (checked: boolean) => {
		emitMixpanelEvent(mixpanelEvents.ACTION_FORM_DASHBOARD_ACCEPTING_TOGGLE_CHANGED, {
			feature: MIXPANEL_APP_FEATURES.DASHBOARD,
			new_status: checked ? 'accepting' : 'not_accepting',
		});

		updateFormStatusMutation({
			form_id: formId,
			status: checked ? 'PUBLISHED' : 'CLOSED',
		});
	};

	return (
		<div
			role="button"
			tabIndex={0}
			onClick={handleCardClick}
			className={cn('w-full cursor-pointer text-left')}
			onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
		>
			<Card
				className={cn(
					'group w-full rounded-none border-0 border-b border-border-light shadow-none hover:bg-muted/50',
					isLastCard && 'mb-16 border-b-0',
					isFocused && 'bg-muted/50'
				)}
			>
				<CardContent className="flex px-4 py-4 md:px-6 md:py-4">
					<div className="min-w-0 flex-1">
						<CardTitle
							className="mb-1.5 truncate text-base tracking-normal md:mb-2 md:text-base"
							title={title || 'Untitled form'}
						>
							{title || 'Untitled form'}
						</CardTitle>
						<FormStatus
							isDraft={isDraft}
							applicationsCount={applicationsCount}
							newCount={newCount}
							lastActivity={lastActivity}
						/>
					</div>

					<div className="flex items-center gap-2 pl-4 md:gap-0">
						{/* Accepting Applications Toggle - Desktop only */}
						{!isDraft && (
							<div
								role="presentation"
								className="mr-2 hidden items-center self-center md:mr-4 md:flex"
								onClick={cancelAnchorNavigation}
								onMouseDown={(e) => {
									e.stopPropagation();
								}}
							>
								<Tooltip
									text={
										formStatus === 'PUBLISHED'
											? 'Currently accepting submissions'
											: 'Not accepting submissions'
									}
								>
									<div className="flex items-center justify-center">
										<Switch
											className="h-4 w-7 data-[state=checked]:bg-primary/80 data-[state=unchecked]:bg-input [&>span]:h-3 [&>span]:w-3 [&>span]:data-[state=checked]:translate-x-3"
											checked={formStatus === 'PUBLISHED'}
											onCheckedChange={handleToggleAcceptingApplications}
											disabled={isUpdatingStatus}
										/>
									</div>
								</Tooltip>
							</div>
						)}

						{/* View and Copy buttons - Hidden on Desktop */}
						<div className="hidden items-center">
							<Tooltip text="View form">
								<Button
									variant="light"
									size="sm"
									className="rounded-r-none text-muted-foreground hover:text-primary group-hover:text-secondary"
									onClick={handleViewForm}
								>
									<ExternalLinkIcon className="size-4" />
								</Button>
							</Tooltip>

							<Tooltip text="Copy form link">
								<Button
									variant="light"
									size="sm"
									className="rounded-none border-l-0 border-r-0 text-muted-foreground hover:text-primary group-hover:text-secondary"
									onClick={handleCopyLink}
								>
									<LinkIcon className="size-4" />
								</Button>
							</Tooltip>
						</div>

						<FormDropdownMenu
							isDraft={isDraft}
							isDropdownOpen={isDropdownOpen}
							setIsDropdownOpen={setIsDropdownOpen}
							handleDuplicate={handleDuplicate}
							handleDelete={handleDelete}
							handleEdit={handleEdit}
							handleViewForm={handleViewForm}
							handleCopyLink={handleCopyLink}
							formStatus={formStatus}
							handleToggleAcceptingApplications={handleToggleAcceptingApplications}
							isUpdatingStatus={isUpdatingStatus}
						/>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

export default memo(FormCard);
